import { useContext, useEffect, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, Check, Input, Pencil, Tags, X } from "../../ui";
import { aiImportText } from "./aiImportText";
import { PROVENANCE_VARIANT, hasValue, isAiField, isLow, needsCheck, percent } from "./aiImportModel";

// Where a value came from. Confidence is only shown where it matters
// (low confidence or a field that needs a human).
export function ProvenanceBadge({ field }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  if (!field?.provenance) return null;
  const showConfidence = isLow(field) || needsCheck(field);
  return (
    <span className="ai-import-badges">
      <Tags variant={PROVENANCE_VARIANT[field.provenance]} size="sm" className={`ai-prov ai-prov-${field.provenance.toLowerCase()}`}>
        {t.provenance[field.provenance]}
      </Tags>
      {needsCheck(field) && (
        <Tags variant="danger" size="sm">
          {t.needsCheck}
        </Tags>
      )}
      {showConfidence && hasValue(field) && field.provenance !== "USER_VERIFIED" && <span className="ai-import-confidence">{t.confidence(percent(field.confidence))}</span>}
    </span>
  );
}

/**
 * One reviewable field.
 *  type: "text" | "longText" | "number"
 *  display(value) -> string shown when not editing
 *  onOp(op) sends setField/verifyField/rejectField for this field
 */
export default function AiImportField({ label, field, type = "text", display, dir, disabled, onOp, allowVerifyEmpty = false }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (!editing) setDraft(field?.value ?? "");
  }, [field, editing]);
  if (!field) return null;
  const shown = display ? display(field.value) : hasValue(field) ? String(field.value) : t.empty;
  const canVerify = field.provenance !== "USER_VERIFIED" && (hasValue(field) || allowVerifyEmpty);
  const save = () => {
    const value = type === "number" ? (String(draft).trim() === "" ? null : Number(draft)) : draft;
    setEditing(false);
    onOp({ op: "setField", value });
  };
  return (
    <div className={`ai-import-field ${needsCheck(field) ? "needs-check" : ""} ${isAiField(field) ? "is-ai" : ""}`}>
      <div className="ai-import-field-head">
        <span className="ai-import-field-label">{label}</span>
        <ProvenanceBadge field={field} />
      </div>
      {editing ? (
        <div className="ai-import-field-edit">
          <Input
            aria-label={label}
            textarea={type === "longText"}
            type={type === "number" ? "number" : "text"}
            inputMode={type === "number" ? "decimal" : undefined}
            dir={dir}
            value={draft ?? ""}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && type !== "longText") save();
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
          />
          <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<Check size={14} />} onClick={save}>
            {t.save}
          </Button>
          <Button type="button" variant="warning" appearance="soft" size="xs" rounded onClick={() => setEditing(false)}>
            {t.cancel}
          </Button>
        </div>
      ) : (
        <div className="ai-import-field-view">
          <span className="ai-import-field-value" dir={dir || "auto"}>
            {shown}
          </span>
          {!disabled && (
            <span className="ai-import-field-actions">
              {canVerify && (
                <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<Check size={13} />} title={isAiField(field) ? t.accept : t.verify} aria-label={`${isAiField(field) ? t.accept : t.verify}: ${label}`} onClick={() => onOp({ op: "verifyField" })} />
              )}
              <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<Pencil size={13} />} title={t.edit} aria-label={`${t.edit}: ${label}`} onClick={() => setEditing(true)} />
              {isAiField(field) && hasValue(field) && (
                <Button type="button" variant="danger" appearance="soft" size="xs" rounded icon={<X size={13} />} title={t.reject} aria-label={`${t.reject}: ${label}`} onClick={() => onOp({ op: "rejectField" })} />
              )}
            </span>
          )}
        </div>
      )}
      {field.original && field.provenance === "USER_VERIFIED" && hasValue(field.original) && <span className="menu-secondary">{t.aiValue(display ? display(field.original.value) : String(field.original.value))}</span>}
    </div>
  );
}
